/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type FC, useCallback, useState } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import EditorDoneIcon from '@atlaskit/icon/core/check-mark';
import CopyIcon from '@atlaskit/icon/core/copy';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import Tooltip from '@atlaskit/tooltip';

const RESET_DELAY = 2000;

const messages = defineMessages({
	copy: {
		id: 'organization.audit.log.side-panel.tab-panels.details.sub-section.json-value.row-value.button',
		defaultMessage: 'Copy',
	},
	copied: {
		id: 'organization.audit.log.side-panel.tab-panels.details.sub-section.json-value.row-value',
		defaultMessage: 'Copied!',
	},
	copyTooltip: {
		id: 'organization.audit.log.side-panel.tab-panels.details.sub-section.copyReadyTooltip.row-value.button',
		defaultMessage: 'Copy to clipboard',
	},
	copySuccessTooltip: {
		id: 'organization.audit.log.side-panel.tab-panels.details.sub-section.copySuccessTooltip.row-value.button',
		defaultMessage: 'Copied!',
	},
	copyFailedTooltip: {
		id: 'organization.audit.log.side-panel.tab-panels.details.sub-section.copyFailedTooltip.row-value.button',
		defaultMessage: 'Copy event JSON failed',
	},
});

enum CopyState {
	READY = 'READY',
	COPIED = 'COPIED',
	FAILED = 'FAILED',
}

interface CopyJsonButtonProps {
	eventJSON: string;
}

export const CopyJsonButton: FC<CopyJsonButtonProps> = ({ eventJSON }) => {
	const { formatMessage } = useIntl();
	const [state, setState] = useState<CopyState>(CopyState.READY);

	const handleCopy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(eventJSON);
			setState(CopyState.COPIED);
		} catch {
			setState(CopyState.FAILED);
		}

		setTimeout(() => setState(CopyState.READY), RESET_DELAY);
	}, [eventJSON]);

	const getIcon = () => {
		switch (state) {
			case CopyState.COPIED:
				return EditorDoneIcon;
			case CopyState.FAILED:
				return StatusErrorIcon;
			default:
				return CopyIcon;
		}
	};

	const getTooltipContent = () => {
		switch (state) {
			case CopyState.COPIED:
				return formatMessage(messages.copySuccessTooltip);
			case CopyState.FAILED:
				return formatMessage(messages.copyFailedTooltip);
			default:
				return formatMessage(messages.copyTooltip);
		}
	};

	const getButtonText = () => {
		return state === CopyState.COPIED ? messages.copied : messages.copy;
	};

	const IconComponent = getIcon();

	return (
		<Tooltip content={getTooltipContent()}>
			<Button
				testId={`copy-button-${state}`}
				iconAfter={IconComponent}
				onClick={handleCopy}
				spacing="compact"
			>
				<FormattedMessage {...getButtonText()} />
			</Button>
		</Tooltip>
	);
};
